import { Workbook } from 'exceljs';
import aegisLog from '@/log/aegis';
import { isEmpty } from '@intelligence/v3/utils/common';
import { getNullableMoment } from '@intelligence/v3/utils/date';
import { generateMetricRatioKey } from '@intelligence/v3/utils/metric';
import { getCountryByCode, getRegionByCode } from '@intelligence/v3/utils/region';
// import { buildGenreFilter } from '@intelligence/v3/utils/genre';
import { SortType } from '@home/enums/common';
import { DATASOURCE_METRIC_KEY_MAP } from '@intelligence/v3/_config/metrics';

import type { StorageParams, ResultObject } from '@intelligence/components/DownloadCommon/DownloadPage/type/type';
import moment from 'moment';

// 目前最大限制数量
export const MAX_ROWS_LIMIT = 100000;

// 获取下载文件名称-通用
export const getFileNameCommon = (params: any) => {
  const startDate = params?.start_date || '';
  const endDate = params?.end_date || '';
  const newStyleDownloadFileName = params?.new_style_donwload_file_name || '';
  if (!!startDate && !!endDate) {
    return `${newStyleDownloadFileName}_${startDate}_${endDate}`;
  }
  return `${newStyleDownloadFileName}`;
};

export const pipe = function (...funcs: any[]) {
  function callback(input: any, func: (arg0: any) => any) {
    return func?.(input);
  }

  return function (param: any) {
    return funcs.reduce(callback, param);
  };
};
// 数据分组
export const groupByData = function (data: any, groupByFields: string[], source: string) {
  if (!groupByFields?.length || !source || !Array.isArray(groupByFields)) return data;
  const cleanData = data
    .map((element: any) => {
      const keyMap: any = {};
      const { [source]: sourceList, ...commonSource } = element;

      sourceList.map((sourceItem: any) => {
        // const keys = sourceItem[groupByField] || ''
        const keys = groupByFields.map((gItem) => sourceItem[gItem]).join('^');
        if (keyMap[keys]) {
          keyMap[keys].push(sourceItem);
        } else {
          keyMap[keys] = [sourceItem];
        }
      });
      // console.log(JSON.stringify(keyMap))
      return Object.values(keyMap).map((oItem) => {
        return {
          ...commonSource,
          [source]: oItem,
        };
      });
    })
    .flat();
  return cleanData;
};
// const disassembleData = (data,keyValue,)=>{}

// 设置浏览器参数
export const setAegisInfoAll = (action: string, storageParams: StorageParams, downloadId: string) => {
  (window as any).aegis?.infoAll?.(
    JSON.stringify({
      action,
      downloadId,
      storageParams,
    }),
  );
};

/**
 * 根据提供的参数和文件名生成最终的文件名。
 *
 * @param params - 一个包含文件名映射的对象，键为文件名，值为对应的文件名字符串。
 * @param $$filename - 一个字符串或字符串数组，表示要生成的文件名。如果为空，则返回默认的 'filename'。
 * @returns 返回生成的文件名。如果 $$filename 是字符串，则直接返回该字符串；如果是字符串数组，则根据 params 中的映射生成文件名。
 *          如果 $$filename 为空或无效，则返回默认的 'filename'。
 */
export const setFileName = (params: Record<string, any>, $$filename: string | string[]): string => {
  if (!$$filename) return 'filename';

  if (typeof $$filename === 'string') return $$filename;

  return $$filename?.map((nameItem) => (nameItem?.length ? params[nameItem] ?? nameItem : '')).join('');
};

// 获取global所有国家
export const getRegionMarket = (params: any) => {
  const { regions, regionOptions } = params || {};
  if (!regions?.length) return undefined;
  if (Array.isArray(regionOptions) && regionOptions?.length > 0) {
    if (regions?.length && !regions?.includes('global')) return regions;
    const marketList = [];
    for (const item of regionOptions) {
      const itemMarket = item.country_set?.map((item: any) => item.country_code);
      marketList.push(...itemMarket);
    }
    marketList.unshift('global');
    return marketList.flat();
  }
  return regions;
};

export const granularityObject: Record<string, string> = {
  daily: 'D',
  weekly: 'W',
  monthly: 'M',
};

// 获取下载文件名称
export const getFileName = (params: any) => {
  const { start_release_time, start_date, end_release_time, end_date, granularity, new_style_donwload_file_name } =
    params;
  const gItem = granularityObject[granularity];
  if (start_date && end_date) {
    return `${new_style_donwload_file_name}_${start_date}_${end_date}`;
  }
  if (start_release_time && end_release_time) {
    return `${new_style_donwload_file_name}_Release_${gItem}${start_release_time}_${gItem}${end_release_time}`;
  }
  return `${new_style_donwload_file_name}`;
};

// 设置下载数据指标表头与对应数据配置
export const mergeData = function (
  data: Record<string, any> = {},
  sourceDownloadSegment?: Record<string, string[]>,
  metricKey = '',
) {
  let result: ResultObject[] = [];
  const segments = sourceDownloadSegment || {};
  // 如果指标直接为下载弹窗勾选的，指标字符串数组，且给了指标字段key
  if (metricKey !== '' && Array.isArray(data[metricKey])) {
    const metricList = data[metricKey] || [];
    result = metricList?.map((metric: any) => {
      return { Metric: metric, Source: '' };
    });
  } else {
    const metrics = data.source_metrics || {};
    // 遍历 source_metric 中的每个键
    Object.keys(metrics).forEach((key) => {
      // 获取当前键对应的 metrics 数组
      const currentMetrics = metrics[key] || [];
      // 获取当前键对应的 segments 数组
      const currentSegments = segments[key] || [];

      // 遍历 metrics 数组
      currentMetrics?.forEach((metric: any) => {
        // 遍历 segments 数组
        if (data.segment_method === 'aggregated' || !data.segment_method) {
          result.push({ Metric: metric, Source: '' });
        } else {
          if (currentSegments?.length) {
            currentSegments?.forEach((segment: any) => {
              // 将结果添加到结果数组中
              result.push({ Metric: metric, Segment: segment, Source: '' });
            });
          } else {
            result.push({ Metric: metric, Segment: '', Source: '' });
          }
        }
      });
    });
  }

  return result;
};

// 对象数组A的元素对象属性 market 按照 对象数组B的元素对象属性 market的顺序排序
// export const sortedArrayA = (arrayA: any, arrayB: any) => {
//   const orderMap = new Map(arrayB?.map((item: any, index: any) => [item.market, index]));
//   console.log('orderMap', orderMap);
//   const sortedArrayA = [...arrayA].sort((a, b) => {
//     const indexA: any = orderMap.get(a.market) ?? Infinity; // 处理不在B中的情况，排在后面
//     const indexB: any = orderMap.get(b.market) ?? Infinity;
//     return indexA - indexB;
//   });
//   return sortedArrayA;
// };

export const getHasNextPageMarketProfile = (totalValue: any, pageValue: any, params: any, downloadNum: any) => {
  const pTotal = pageValue * 500;
  // console.log('pageValuepageValue', pageValue);
  // console.log('params?.select_top', params?.select_top);
  // console.log('downloadNum', downloadNum);
  // console.log('totalValue', totalValue);
  // console.log('pTotal', pTotal);
  if (pTotal >= MAX_ROWS_LIMIT) return false;
  // downloadNum 已下载多少个
  // totalValue 后端接口查询共有多少个
  // 目标个数smallerMaxNum
  const smallerMaxNum = Math.min(totalValue, params?.select_top);
  // console.log('smallerMaxNum', smallerMaxNum);
  // 还剩多少个remaNum
  const remaNum = smallerMaxNum - downloadNum;
  // 本次下载多少个remaNumCur
  const reMaNumCur = remaNum > 500 ? 500 : remaNum;

  // console.log('remaNum', remaNum);
  // console.log('wefwef', {
  //   hasNextPage: pTotal < smallerMaxNum,
  //   reMaNumCur,
  // });
  return {
    hasNextPage: pTotal < smallerMaxNum,
    reMaNumCur,
  };
};

export const getMetricFilter = (list: any, params: any) => {
  return list?.filter((item: any) => {
    for (const filter of params?.metric_filter) {
      if (caculate(item, filter?.metric, filter?.opt, filter?.value)) {
        // 因为是or关系，任意命中即返回
        return true;
      }
    }
    return false;
  });
};

function caculate(left: any, metric: any, op: any, right: any) {
  let l = Number(left?.[`metrics_value_${metric}`]);
  if (isNaN(l)) {
    l = 0;
  }
  right = ensureRight(right);
  switch (op) {
    case '>':
      return l > right;
    case '>=':
      return l >= right;
    case '<':
      return l < right;
    case '<=':
      return l <= right;
    case '=':
      return l === right;
    case '!=':
      return l !== right;
    default:
      return false;
  }
}
function ensureRight(right: any) {
  right = right / 100;
  if (isNaN(right)) {
    right = 0;
  }
  return right;
}

export const isIncludesSegmentValue = (segment: string, params: any, source = 'mscience') => {
  if (!params.b_group_by_segment) return false;
  const mscienceSegmentList = params?.metric_condition?.mscience?.segments || [];
  const gsdSegmentList = params?.metric_condition?.gsd?.segments || [];

  const sourceSegmentList = source === 'gsd' ? gsdSegmentList : mscienceSegmentList;

  if (Array.isArray(sourceSegmentList) && sourceSegmentList?.length > 0) {
    return sourceSegmentList?.includes(segment);
  }
  return false;
};

export const compareGameId = (gameIdList: string[], a?: any, b?: any) => {
  const aGameId = a?.game_id || '';
  const bGameId = b?.game_id || '';
  const aGameSortIndex = gameIdList?.indexOf(aGameId) ?? -1;
  const bGameSortIndex = gameIdList?.indexOf(bGameId) ?? -1;
  if (aGameSortIndex === -1 && bGameSortIndex === -1) return 0;
  if (aGameSortIndex === -1) return 1;
  if (bGameSortIndex === -1) return -1;
  return aGameSortIndex - bGameSortIndex;
};

/**
 * 字符串升序比较（用于地区、国家按字母排序）
 * @param a 第一个字符串
 * @param b 第二个字符串
 * @returns 比较结果
 */
export const compareStringAsc = (a?: string, b?: string) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
};

/**
 * 通用实体ID比较方法
 * @param entityKey 实体ID字段名，如 'game_id'、'company_id'
 * @param entityIds 实体ID顺序列表
 * @param a 第一个对象
 * @param b 第二个对象
 * @returns 比较结果
 */
export const compareEntityId = (entityKey: string, entityIds: string[], a?: any, b?: any) => {
  const aEntityId = a?.[entityKey] || '';
  const bEntityId = b?.[entityKey] || '';
  const aSortIndex = entityIds?.indexOf(aEntityId) ?? -1;
  const bSortIndex = entityIds?.indexOf(bEntityId) ?? -1;

  if (aSortIndex === -1 && bSortIndex === -1) return 0;
  if (aSortIndex === -1) return 1;
  if (bSortIndex === -1) return -1;
  return aSortIndex - bSortIndex;
};

/**
 * 排序要素配置接口
 */
interface IDownloadSortConfig {
  /** 实体ID字段名，如 'game_id'、'company_id' */
  entityKey: string;
  /** 实体ID顺序列表（可选，不传则按数据中实体ID首次出现的顺序） */
  entityIds?: string[];
  /** 是否按日期拆分 */
  isGroupByDate?: boolean;
  /** 地区拆分方式：'aggregated' | 'region' | 'market' */
  regionMethod?: string;
  /** 地区字段名（可选，默认 'region'） */
  regionKey?: string;
  /** 国家字段名（可选，默认 'market'） */
  marketKey?: string;
}

/**
 * 通用下载数据排序方法
 * @description 根据拆分方式动态构建排序逻辑
 * @param list 待排序的数据列表
 * @param config 排序配置
 *
 * 排序优先级：
 * 1. 实体排序（entityIds 顺序，不传则按数据中实体ID首次出现的顺序）
 * 2. 日期降序（仅当 isGroupByDate=true）
 * 3. 地区排序（仅当 regionMethod='region' 或 'market'，按字母顺序）
 * 4. 国家排序（仅当 regionMethod='market'，按字母顺序）
 */
export const sortDownloadDataByConfig = (list: any[], config: IDownloadSortConfig) => {
  if (!Array.isArray(list) || list.length === 0) return list;

  const { entityKey, entityIds, isGroupByDate, regionMethod, regionKey = 'region', marketKey = 'market' } = config;

  // 判断是否需要地区/国家排序
  const needRegionSort = regionMethod === 'region' || regionMethod === 'market';
  const needMarketSort = regionMethod === 'market';

  // 如果没有提供 entityIds，从数据中提取实体ID的出现顺序
  const finalEntityIds = entityIds && entityIds.length > 0 ? entityIds : extractEntityIdsFromList(list, entityKey);

  return list.sort((a, b) => {
    // 1. 实体排序（按实体ID的出现顺序）
    if (finalEntityIds && finalEntityIds.length > 0) {
      const entityCompareResult = compareEntityId(entityKey, finalEntityIds, a, b);
      if (entityCompareResult !== 0) return entityCompareResult;
    }

    // 2. 日期降序（仅当按日期拆分时）
    if (isGroupByDate) {
      const dateCompareResult = compareDate(a?.date, b?.date, SortType.Desc);
      if (dateCompareResult !== 0) return dateCompareResult;
    }

    // 3. 地区排序（按字母顺序）
    if (needRegionSort) {
      const regionCompareResult = compareStringAsc(a?.[regionKey], b?.[regionKey]);
      if (regionCompareResult !== 0) return regionCompareResult;
    }

    // 4. 国家排序（按字母顺序）
    if (needMarketSort) {
      const marketCompareResult = compareStringAsc(a?.[marketKey], b?.[marketKey]);
      if (marketCompareResult !== 0) return marketCompareResult;
    }

    return 0;
  });
};

/**
 * 从数据列表中提取实体ID的出现顺序
 * @description 按数据中实体ID首次出现的顺序，保持原始分组
 */
const extractEntityIdsFromList = (list: any[], entityKey: string): string[] => {
  const entityIdSet = new Set<string>();
  list.forEach((item) => {
    const entityId = item?.[entityKey];
    if (entityId && !entityIdSet.has(entityId)) {
      entityIdSet.add(entityId);
    }
  });
  return Array.from(entityIdSet);
};

/**
 * 比较两个对象的 market 字段在 markets 数组中的顺序
 * @param markets - 市场顺序数组
 * @param a - 第一个对象
 * @param b - 第二个对象
 * @returns 比较结果
 * @date 2025-11-05
 * @description 按照 markets 数组中的顺序对对象进行排序
 * @author lv.chen
 */
export const compareMarket = (markets: string[], a?: any, b?: any) => {
  const aMarket = a?.market || '';
  const bMarket = b?.market || '';
  const aMarketSortIndex = markets?.indexOf(aMarket) ?? -1;
  const bMarketSortIndex = markets?.indexOf(bMarket) ?? -1;
  if (aMarketSortIndex === -1 && bMarketSortIndex === -1) return 0;
  if (aMarketSortIndex === -1) return 1;
  if (bMarketSortIndex === -1) return -1;
  return aMarketSortIndex - bMarketSortIndex;
};

export const compareDate = (a?: string, b?: string, sortType = SortType.Desc) => {
  const aDateM = getNullableMoment(a);
  const bDateM = getNullableMoment(b);
  if (aDateM === null && bDateM === null) return 0;
  if (aDateM === null) return 1;
  if (bDateM === null) return -1;
  if (aDateM.isSame(bDateM)) return 0;
  const result = aDateM.isAfter(bDateM) ? -1 : 1;
  return sortType === SortType.Desc ? result : -result;
};

export const sortDownloadGamesDataCommon = (sortGameIds: string[], games?: any[]) => {
  if (!Array.isArray(games) || games.length === 0) return;

  games.sort((a: any, b: any) => {
    const gameIdCompareResult = compareGameId(sortGameIds, a, b);
    if (gameIdCompareResult !== 0) return gameIdCompareResult;

    const gameDateCompareResult = compareDate(a?.date, b?.date);
    if (gameDateCompareResult !== 0) return gameDateCompareResult;

    return 0;
  });
};

/**
 * 通用下载数据排序方法
 * 第一排序：按游戏顺序排序
 * 第二排序：按国家顺序排序
 * 第三排序：按date降序
 * @param games 游戏指标数据
 * @param config 其他参数
 * @param config.gameIds 游戏id数组
 * @param config.markets 国家数组
 */
export const sortDownloadGamesMetrics = (
  games?: any[],
  config?: { gameIds?: string[] | null; markets?: string[] | null },
) => {
  if (isEmpty(games)) {
    return;
  }

  const gameIds = config?.gameIds || [];
  const markets = config?.markets || [];

  games!.sort((a, b) => {
    const gameIdCompareResult = compareGameId(gameIds, a, b);
    if (gameIdCompareResult !== 0) return gameIdCompareResult;

    const marketCompareResult = compareMarket(markets, a, b);
    if (marketCompareResult !== 0) return marketCompareResult;

    const gameDateCompareResult = compareDate(a?.date, b?.date);
    if (gameDateCompareResult !== 0) return gameDateCompareResult;

    return 0;
  });
};

export const groupByCrossAppMetric = (list?: any[] | null, ext?: IRecord): any[] => {
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }

  const entityInfoMap = ext?.entity_info || {};
  const crossAppUsageMetricKey = DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_APP_USAGE;
  const crossAppUsageMetricRatioKey = generateMetricRatioKey(crossAppUsageMetricKey);
  const crossAffinityMetricKey = DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_AFFINITY;
  const crossAffinityMetricRatioKey = generateMetricRatioKey(crossAffinityMetricKey);

  const newList: any[] = [];

  for (const item of list) {
    const crossAppUsageGames = item?.[crossAppUsageMetricKey] || [];
    const crossAppUsageGamesIds = crossAppUsageGames?.map((item: any) => item?.target_game_id || '') || [];
    const crossAffinityGames = item?.[crossAffinityMetricKey] || [];
    const crossAffinityGamesIds = crossAffinityGames?.map((item: any) => item?.target_game_id || '') || [];
    const crossGameIds = Array.from(new Set([...crossAppUsageGamesIds, ...crossAffinityGamesIds]));
    const crossGames = [...crossAppUsageGames, ...crossAffinityGames];

    // if (!Array.isArray(crossGameIds) || crossGameIds.length === 0) {
    //   newList.push({
    //     [crossAppUsageMetricKey]: '',
    //     [crossAppUsageMetricRatioKey]: '',
    //     [crossAffinityMetricKey]: '',
    //     [crossAffinityMetricRatioKey]: '',
    //   });
    //   continue;
    // }

    for (const crossGameId of crossGameIds) {
      const crossGameDetails = entityInfoMap?.[crossGameId] || {};
      const crossGame = crossGames?.find((item: any) => item?.target_game_id === crossGameId);
      newList.push({
        ...item,
        compared_game_name: crossGameDetails?.entity_name || '',
        [crossAppUsageMetricKey]: crossGame?.[DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_APP_USAGE] || '',
        [crossAppUsageMetricRatioKey]:
          crossGame?.[generateMetricRatioKey(DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_APP_USAGE)] || '',
        [crossAffinityMetricKey]: crossGame?.[DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_AFFINITY] || '',
        [crossAffinityMetricRatioKey]:
          crossGame?.[generateMetricRatioKey(DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_AFFINITY)] || '',
      });
    }
  }

  return newList;
};

export const groupByOverlapDetailsCrossAppMetric = (
  list?: any[] | null,
  ext?: IRecord | null,
  // params?: IRecord | null,
): { gameIds: string[]; gameMetricsData: any[] } => {
  if (!Array.isArray(list) || list.length === 0) {
    return { gameIds: [], gameMetricsData: [] };
  }

  // const genreFilter = buildGenreFilter(params);
  const entityInfoMap = ext?.entity_info || {};
  const crossAppUsageMetricKey = DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_APP_USAGE;
  const crossAppUsageMetricRatioKey = generateMetricRatioKey(crossAppUsageMetricKey);
  const crossAffinityMetricKey = DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_AFFINITY;
  const crossAffinityMetricRatioKey = generateMetricRatioKey(crossAffinityMetricKey);
  const gameIdSet = new Set<string>();
  const map: IRecord = {};

  for (const item of list) {
    const crossAppUsageGames = item?.[crossAppUsageMetricKey] || [];
    const crossAppUsageGamesIds = crossAppUsageGames?.map((item: any) => item?.target_game_id || '') || [];
    const crossAffinityGames = item?.[crossAffinityMetricKey] || [];
    const crossAffinityGamesIds = crossAffinityGames?.map((item: any) => item?.target_game_id || '') || [];
    const crossGameIds = Array.from(new Set([...crossAppUsageGamesIds, ...crossAffinityGamesIds]));
    const crossGames = [...crossAppUsageGames, ...crossAffinityGames];

    for (const crossGameId of crossGameIds) {
      const crossGameDetails = entityInfoMap?.[crossGameId] || {};
      const crossGame = crossGames?.find((item: any) => item?.target_game_id === crossGameId);
      const crossGameMarket = crossGame?.market || '';
      const crossGameDate = crossGame?.date || '';
      const key = `${crossGameMarket}__${crossGameDate}__${crossGameId}`;
      const metricKeys = [
        crossAppUsageMetricKey,
        crossAppUsageMetricRatioKey,
        crossAffinityMetricKey,
        crossAffinityMetricRatioKey,
      ];

      // if (!genreFilter(crossGameDetails)) {
      //   continue;
      // }

      gameIdSet.add(crossGameId);

      if (!map[key]) {
        map[key] = {
          ...item,
          ...crossGameDetails,
          game_id: crossGameId || '',
          game_name: crossGameDetails?.entity_name || '',
          date: crossGame?.date || item?.date || '',
          market: crossGameMarket || item?.market || '',
          rank: item?.rank ?? item?.rank ?? 0,
        };
      }

      metricKeys.forEach((metricKey) => {
        const metricValue = crossGame?.[metricKey];
        if (!!metricValue) {
          map[key][metricKey] = metricValue;
        }
      });
    }
  }

  return {
    gameIds: Array.from(gameIdSet),
    gameMetricsData: Object.values(map),
  };
};

export const mergeGameDetailsData = (list?: any[] | null, detailsMap?: IRecord<IRecord> | null): IRecord[] => {
  if (isEmpty(list)) return [];
  if (isEmpty(detailsMap)) return list!;

  return list!.map((item) => {
    const gameId = item?.game_id || '';
    const gameDetails = detailsMap![gameId];
    return { ...item, ...gameDetails };
  });
};

export const mergeCommonDetailsData = (
  list: any[],
  detailsMap?: IRecord<IRecord> | null,
  isByTime = false,
  detailKey?: string,
) => {
  if (!Array.isArray(list) || isEmpty(list)) return [];
  if (isEmpty(detailsMap) && !detailKey) return list!;
  let newList = [...list].filter((item) => item && typeof item === 'object');
  if (isEmpty(newList)) return [];
  if (detailKey) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    newList = newList.map((item) => {
      const detailId = item?.[detailKey] || '';
      const companyDetails = detailsMap?.[detailId] || {};
      return { ...item, ...companyDetails };
    });
  }
  // 如果isByTime为true，则把newList先按键名为detailKey的属性相同的排在一起，然后再按date的desc排序，最后组成一个新的数组
  if (isByTime || detailKey === 'sub_genre') {
    if (!detailKey) {
      newList = [...newList].sort((a, b) => compareDate(a?.date, b?.date, SortType.Desc));
    } else {
      let detailKeyValue = detailKey;
      if (detailKey === 'sub_genre') {
        detailKeyValue = 'genre';
      }
      const groupedMap = new Map<any, any[]>();
      newList.forEach((item) => {
        const groupKey = item?.[detailKeyValue] || '';
        const groupedList = groupedMap.get(groupKey) || [];
        groupedList.push(item);
        groupedMap.set(groupKey, groupedList);
      });
      newList = Array.from(groupedMap.values()).flatMap((groupedList) => {
        return groupedList.sort((a, b) => compareDate(a?.date, b?.date, SortType.Desc));
      });
    }
  }
  return newList;
};

export const getFileFormatName = (fileFormat?: number) => {
  if (fileFormat === undefined || fileFormat === null || Number.isNaN(fileFormat)) {
    return '';
  }

  return fileFormat === 0 ? 'Excel' : 'CSV';
};

/**
 * 获取下载的文件名，文件名格式为：名称_开始日期_结束日期
 * @param params 下载的参数对象
 */
export const getDateRangeFileName = (params?: IRecord | null) => {
  const startDate = params?.start_date || '';
  const endDate = params?.end_date || '';
  const newStyleDownloadFileName = params?.new_style_donwload_file_name || '';

  if (!!startDate && !!endDate) {
    return `${newStyleDownloadFileName}_${startDate}_${endDate}`;
  }

  return `${newStyleDownloadFileName}`;
};

// 检测是否为下载空数据时，需要忽略的EXCEL标签页
const IGNORE_CHECK_SHEET_NAMES = ['Summary'];

/**
 * 检测并上报空数据情况
 * @param workbook - Excel工作簿对象
 * @param option - 其他参数
 * @param option.downloadKey - 下载类型标识
 * @param option.downloadId - 下载ID
 * @param option.params - 下载参数
 * @param option.fileName - 文件名
 * @returns 返回检测结果，包含是否为空、总行数、各sheet统计信息
 */
export const checkAndReportEmptyData = (
  workbook: Workbook,
  option: {
    downloadKey: string;
    downloadId: string;
    fileName: string;
    params: any;
  },
) => {
  const { downloadKey, downloadId, fileName, params } = option || {};

  let totalRows = 0;
  const sheetStats: IRecord<number> = {};

  // 统计每个sheet的数据行数（排除表头）
  workbook?.eachSheet((worksheet) => {
    const worksheetName = worksheet?.name || '';
    if (IGNORE_CHECK_SHEET_NAMES.includes(worksheetName)) {
      return;
    }
    const worksheetRowCount = worksheet?.rowCount || 0;
    // 减去表头
    const rowCount = worksheetRowCount > 0 ? worksheetRowCount - 1 : 0;
    sheetStats[worksheetName] = rowCount;
    totalRows += rowCount;
  });

  // 如果总行数为0，上报空数据
  if (totalRows === 0) {
    const baseLogInfo = {
      url: location.href,
      timestamp: Date.now(),
    };
    const downloadLogInfo = {
      downloadKey,
      downloadId,
      fileName,
      sheetStats,
      params: JSON.stringify(params),
    };
    aegisLog.reportEvent({
      name: 'metrics_empty_detected',
      msg: 'detected empty data download',
      ext1: JSON.stringify(baseLogInfo),
      ext2: JSON.stringify(downloadLogInfo),
      // ext3: '',
    });

    console.warn('[Empty Data Download]', {
      downloadKey,
      fileName,
      sheetStats,
    });
  }

  return { isEmpty: totalRows === 0, totalRows, sheetStats };
};

/**
 * 根据下载时设置的地区集合方式以及接口返回数据地区code（国家code）,查询地区国家信息
 * 按地区拆分时，code是地区code
 * 按国家拆分时，code是国家code
 * @param params - 参数对象
 * @param params.regionMethod - 聚合方式：聚合、按地区拆分、按国家拆分
 * @param params.options - 地区国家选项数据
 * @param params.code - 地区code（国家code）
 * @return 地区国家信息
 */
export const getRegionCountryData = (params?: {
  regionMethod?: string;
  options?: IRegion[] | null;
  code?: string;
}): {
  region_english: string;
  region_chinese: string;
  country_english: string;
  country_chinese: string;
} => {
  const { regionMethod, options, code } = params || {};

  const countryAndRegion = {
    region_english: '',
    region_chinese: '',
    country_english: '',
    country_chinese: '',
  };

  if (regionMethod === 'region') {
    const region = getRegionByCode(code, options);
    countryAndRegion.region_english = region?.area_name_en || '';
    countryAndRegion.region_chinese = region?.area_name_ch || '';
  }

  if (regionMethod === 'market') {
    const country = getCountryByCode(code, options);
    countryAndRegion.region_english = country?.area_name_en || '';
    countryAndRegion.region_chinese = country?.area_name_ch || '';
    countryAndRegion.country_english = country?.country_name_en || '';
    countryAndRegion.country_chinese = country?.country_name_ch || '';
  }

  return countryAndRegion;
};

// 获取正确的时间格式，主要是month
export const getFormatDate = (date: any, type: string) => {
  if (type === 'month' || type === 'monthly') {
    return moment(date || '')?.format('YYYY-MM');
  }
  return date;
};
